/**
 * VoucherClaimCard - Shared presentational component for voucher batch details.
 *
 * Shows merchant name, token amount (fiat primary), availability progress bar,
 * memo, label, and expiration. Pure props — no data fetching.
 */

import React from 'react';

export interface VoucherClaimCardProps {
  merchantName: string;
  tokenName: string;
  tokenSymbol: string;
  amountPerVoucher: number;
  fiatValue: number | null;
  currency: string;
  quantity: number;
  vouchersAvailable: number;
  memo?: string | null;
  label?: string | null;
  expiresAt?: string | null;
  status: string;
}

function formatCurrencyValue(value: number, currency: string): string {
  const symbols: Record<string, string> = {
    USD: '$',
    EUR: '\u20AC',
    GBP: '\u00A3',
    CAD: 'CA$',
    AUD: 'A$',
  };
  const symbol = symbols[currency.toUpperCase()] || `${currency} `;
  return `${symbol}${value.toFixed(2)}`;
}

export const VoucherClaimCard: React.FC<VoucherClaimCardProps> = ({
  merchantName,
  tokenName,
  tokenSymbol,
  amountPerVoucher,
  fiatValue,
  currency,
  quantity,
  vouchersAvailable,
  memo,
  label,
  expiresAt,
}) => {
  const progressPct =
    quantity > 0
      ? Math.min(100, Math.max(0, ((quantity - vouchersAvailable) / quantity) * 100))
      : 0;

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
      {/* Merchant */}
      <div className="mb-6">
        <p className="text-sm text-gray-500 mb-1">From</p>
        <p className="text-xl font-semibold text-gray-900">{merchantName}</p>
      </div>

      {/* Amount */}
      <div className="mb-6 py-6 border-t border-b border-gray-200">
        <p className="text-sm text-gray-500 mb-2">Amount per voucher</p>
        {fiatValue !== null ? (
          <>
            <p className="text-4xl font-bold text-blue-600">
              {formatCurrencyValue(fiatValue, currency)}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              {amountPerVoucher.toLocaleString()} {tokenName} ({tokenSymbol})
            </p>
          </>
        ) : (
          <p className="text-4xl font-bold text-blue-600">
            {amountPerVoucher.toLocaleString()} {tokenSymbol}
          </p>
        )}
      </div>

      {/* Memo */}
      {memo && (
        <div className="mb-6">
          <p className="text-sm text-gray-500 mb-1">Message</p>
          <p className="text-base text-gray-900 italic">
            &quot;{memo}&quot;
          </p>
        </div>
      )}

      {/* Availability */}
      <div className="mb-4">
        <div className="flex justify-between text-sm mb-2">
          <span className="text-gray-600">Availability</span>
          <span className="font-medium text-gray-900">
            {quantity - vouchersAvailable >= quantity * 0.5
              ? `${vouchersAvailable} of ${quantity} remaining`
              : `${vouchersAvailable} more remaining`}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className="bg-blue-600 h-3 rounded-full transition-all duration-300"
            style={{ width: `${100 - progressPct}%` }}
          />
        </div>
      </div>

      {/* Label */}
      {label && (
        <div className="mb-4">
          <span className="inline-block bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
            {label}
          </span>
        </div>
      )}

      {/* Expiration */}
      {expiresAt && (
        <div>
          <p className="text-xs text-gray-500 mb-1">Expires</p>
          <p className="text-sm text-red-600 font-medium">
            {new Date(expiresAt).toLocaleDateString()} at{' '}
            {new Date(expiresAt).toLocaleTimeString()}
          </p>
        </div>
      )}
    </div>
  );
};
